import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { RestApi, ApiResponse } from '../rest_api/rest_api';

@Injectable({
    providedIn: 'root',
})

export class LiveBusDataProvider {

    private pollTimer: any;
    private fetchInFlight = false;
    private busStop: string = 'LANCBS001';
    private maxDepartures: number = 200;
    private consecutiveNetworkFailures = 0;
    private consecutive429s = 0;
    private unix_now: number;
    private tll: number;
    private access_token: string;
    private DATA_POINTER: string = '_instancelist';
    public data: BehaviorSubject<any> = new BehaviorSubject<any>([]);
    public lastFetchSuccess: number = 0;

    private static readonly NORMAL_INTERVAL_MS = 10000;
    private static readonly FAST_RETRY_INTERVAL_MS = 1000;
    private static readonly FAST_RETRY_BURST_COUNT = 10;
    private static readonly AUTH_BACKOFF_BASE_MS = 10000;
    private static readonly AUTH_BACKOFF_MAX_MS = 60000;
    private static readonly RATE_LIMIT_BACKOFF_BASE_MS = 2000;
    private static readonly RATE_LIMIT_BACKOFF_MAX_MS = 60000;

    constructor(
        private restApi: RestApi
    ) { }

    // Load from local storage
    load(pulling = false, busStop, maxDepartures) {
        // Get timestamp
        this.unix_now = Date.now();
        this.busStop = busStop;
        this.maxDepartures = maxDepartures;

        if (pulling) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
            this.runPollLoop(0);
        } else {
            this.fetchUpdate();
        }
    }

    stopAutoPull(clearCache = true) {
        //clear the intervall if leaving this view
        var msg = 'Stop Intervall';
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
        this.consecutiveNetworkFailures = 0;
        this.consecutive429s = 0;
        if (clearCache) {
            this.data.next([]);
            this.lastFetchSuccess = 0;
            msg += ' and cleard cache';
        }
    }

    startAutoPull() {
        if (!this.pollTimer) {
            this.runPollLoop(0);
        }
    }

    private runPollLoop(delay: number) {
        this.pollTimer = setTimeout(async () => {
            const nextDelay = await this.fetchUpdate();
            this.runPollLoop(nextDelay);
        }, delay);
    }

    async fetchUpdate(): Promise<number> {
        if (this.fetchInFlight) {
            return LiveBusDataProvider.NORMAL_INTERVAL_MS;
        }

        this.fetchInFlight = true;
        try {
            return await this.doFetch();
        } finally {
            this.fetchInFlight = false;
        }
    }

    private async doFetch(): Promise<number> {
        let apiResponse: ApiResponse = await this.restApi.getBusData(this.busStop, this.maxDepartures);
        if (apiResponse.ok) {
            this.lastFetchSuccess = Date.now();
            this.consecutiveNetworkFailures = 0;
            this.consecutive429s = 0;
            this.data.next(apiResponse);
            console.log('Bus data updated', this.busStop, apiResponse.body?.length ?? 0);
            return LiveBusDataProvider.NORMAL_INTERVAL_MS;
        }

        console.error('Error fetching bus data');
        console.error(apiResponse);
        console.error(JSON.stringify(apiResponse));

        if (apiResponse.status === 429) {
            this.consecutive429s++;
            return Math.min(
                LiveBusDataProvider.RATE_LIMIT_BACKOFF_BASE_MS * 2 ** (this.consecutive429s - 1),
                LiveBusDataProvider.RATE_LIMIT_BACKOFF_MAX_MS
            );
        }
        this.consecutive429s = 0;

        if (apiResponse.status === 401) {
            this.consecutiveNetworkFailures++;
            return Math.min(
                LiveBusDataProvider.AUTH_BACKOFF_BASE_MS * 2 ** (this.consecutiveNetworkFailures - 1),
                LiveBusDataProvider.AUTH_BACKOFF_MAX_MS
            );
        }

        if (apiResponse.status === 0 || apiResponse.status >= 500) {
            this.consecutiveNetworkFailures++;
            if (this.consecutiveNetworkFailures <= LiveBusDataProvider.FAST_RETRY_BURST_COUNT) {
                return LiveBusDataProvider.FAST_RETRY_INTERVAL_MS;
            }

            return Math.min(
                LiveBusDataProvider.FAST_RETRY_INTERVAL_MS * (this.consecutiveNetworkFailures - LiveBusDataProvider.FAST_RETRY_BURST_COUNT + 1),
                LiveBusDataProvider.NORMAL_INTERVAL_MS
            );
        }

        return LiveBusDataProvider.NORMAL_INTERVAL_MS;
    }


}
